import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import { type TrialDto, type TrialListQuery } from '@ctms/contracts';
import { FlaskConical } from 'lucide-react';

import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

export interface ActiveTrialPickerProps {
  canRead: boolean;
  /** The trial the cockpit works on. `null` means "nothing picked" and shows the empty option. */
  activeTrialId: number | null;
  onSelect: (trial: TrialDto | null) => void;
}

/**
 * One page is enough for the working list of a site — `active=true` is the only
 * source, the API caps `limit` at 100 (REQ-VIS-005, first clause: picking a
 * trial sets the active one and reloads the participant list).
 */
const PICKER_QUERY = { page: 1, limit: 100, active: true } as const satisfies TrialListQuery;

/** Shown as the first option — picking it clears the active trial. */
export const NO_ACTIVE_TRIAL_LABEL = '— brak —';

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function optionLabel(trial: TrialDto): string {
  return `${trial.shortcut} — ${trial.shortTitle}`;
}

export function ActiveTrialPicker({
  canRead,
  activeTrialId,
  onSelect,
}: ActiveTrialPickerProps): JSX.Element | null {
  const trialsQuery = useQuery({
    queryKey: trialQueryKeys.list(PICKER_QUERY),
    queryFn: () => trialsApi.listTrials(PICKER_QUERY),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  // The UI hides it, the backend enforces it — a user without trials read never sees the picker.
  if (!canRead) {
    return null;
  }

  const trials = trialsQuery.data?.items ?? [];

  return (
    <label className="flex items-center gap-2 text-xs text-slate-400">
      <FlaskConical className="h-3.5 w-3.5 text-cyan-400" />
      Badanie
      <select
        value={activeTrialId === null ? '' : String(activeTrialId)}
        onChange={(event) => {
          const chosen = event.target.value;
          onSelect(trials.find((trial) => String(trial.id) === chosen) ?? null);
        }}
        className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark"
      >
        <option value="">{NO_ACTIVE_TRIAL_LABEL}</option>
        {trials.map((trial) => (
          <option key={trial.id} value={String(trial.id)}>
            {optionLabel(trial)}
          </option>
        ))}
      </select>
    </label>
  );
}